import axios, { AxiosResponse } from 'axios';
import RestX from 'highered-utils/restx';
import URLS from '~/URLS';
import { objectToQueryString } from '~/utils/urlUtils';
import AnalyticService from './AnalyticService';

// Services
import BillingService, { Product, CheckoutSessionParams } from './BillingService';
import DialogService from './DialogService';

// Constant
const API_URL: string = process.env.VUE_APP_API_URL.concat('v1/');
export const INSTRUMENTS_PREMIUM = [4941, 390, 5392, 82013];
export const SAMPLE_REPORT_URL: string = `${API_URL}candidate/test/aon/samplereport/`;

export enum InstrumentStatus {
    UNKNOWN = -1,
    INCOMPLETE = 0,
    NEW = 1,
    IN_PROGRESS = 2,
    PROCESSING = 3,
    COMPLETE = 4,
    BLOCKED = 5,
}

export type InstrumentData = {
    id: Number;
    name: string;
    status: InstrumentStatus;
    description?: string;
    estimatedTime?: Number;
};

export type AonCandidateData = {
    link: string;
    linkExpireAt: string;
    reportLink: string;
    reportLinkExpireAt: string;
    instruments: InstrumentData[];
};

export type AonContextData = {
    id: string;
    candidateId: string;
    aonKey: string;
    consented: boolean;
    freemium: AonCandidateData;
    premium?: AonCandidateData;
};

export type ContextParams = {
    include_instruments?: boolean;
    include_report_link?: boolean;
};

enum AonLinkType {
    FreemiumHub,
    PremiumHub,
    PremiumReport,
}

export default class AonContext {
    data: AonContextData;

    constructor(data: AonContextData) {
        this.data = data;
    }

    get hasConsented(): boolean {
        return this.data.consented === true;
    }

    get hasPremium(): boolean {
        return Boolean(this.data.premium);
    }

    get freemiumStatus(): InstrumentStatus {
        if (!this.data.freemium.instruments) {
            return InstrumentStatus.UNKNOWN;
        }

        return this.data.freemium.instruments.reduce<InstrumentStatus>(
            (acc, instr) => (instr.status > acc ? instr.status : acc),
            InstrumentStatus.UNKNOWN
        );
    }

    get premiumStatus(): InstrumentStatus {
        if (!this.hasPremium || !this.data.premium) {
            return InstrumentStatus.UNKNOWN;
        }
        const isComplete = this.data.premium.instruments.every(
            instr => instr.status === InstrumentStatus.COMPLETE
        );
        const isPending = this.data.premium.instruments.some(
            instr =>
                instr.status >= InstrumentStatus.IN_PROGRESS &&
                instr.status < InstrumentStatus.COMPLETE
        );
        return isComplete
            ? InstrumentStatus.COMPLETE
            : isPending
            ? InstrumentStatus.IN_PROGRESS
            : InstrumentStatus.NEW;
    }

    updateData(ctxData: AonContextData) {
        this.data = ctxData;
    }

    openFreemium() {
        this.openLink(AonLinkType.FreemiumHub);
    }

    openPremium(): Promise<void> {
        if (!this.hasPremium) return this.upgradeToPremium();

        this.openLink(AonLinkType.PremiumHub);
        return Promise.resolve();
    }

    openInstrument(instr: InstrumentData, isPremium: boolean = false): Promise<void> {
        if (!isPremium) {
            this.openFreemium();
            return Promise.resolve();
        }

        switch (instr.status) {
            case InstrumentStatus.UNKNOWN:
            case InstrumentStatus.NEW:
            case InstrumentStatus.IN_PROGRESS:
            case InstrumentStatus.BLOCKED:
            case InstrumentStatus.PROCESSING: {
                this.openPremium();
                return Promise.resolve();
            }
            case InstrumentStatus.COMPLETE: {
                // Ask for report-link
            }
        }

        // Something went wrong. Nothing happened?
        return Promise.reject();
    }

    openPremiumReport(): Promise<void> {
        if (!this.hasPremium) {
            return Promise.reject();
        }

        return this.refresh(
            {
                include_report_link: true,
            },
            false
        ).then((ctxData: AonContextData) => {
            if (!ctxData.premium) {
                return;
            }
            this.data.premium = {
                ...((this.data.premium || {}) as AonCandidateData),
                reportLink: ctxData.premium.reportLink,
                reportLinkExpireAt: ctxData.premium.reportLinkExpireAt,
            };
            this.openLink(AonLinkType.PremiumReport);
        });
    }

    getInstrumentStatus(instrId: number, isPremium: boolean = false): InstrumentStatus {
        let instrumentData: InstrumentData[] = this.data.freemium.instruments || [];
        if (isPremium) {
            instrumentData = this.data.premium ? this.data.premium.instruments : [];
        }

        const instrument = instrumentData.find(instr => instr.id === instrId);
        if (!instrument) {
            return InstrumentStatus.UNKNOWN;
        }
        return instrument.status;
    }

    async upgradeToPremium(): Promise<void> {
        // Get products related to aon
        const products = await BillingService.getProductsByTags({
            category: 'aon',
            version: 'v1',
        });
        if (products.length === 0) {
            return Promise.reject();
        }
        const product = products[0];

        const returnUrl = window.location.origin.concat(URLS.assessments, URLS.assessmentsRedirect);
        const params: CheckoutSessionParams = {
            items: [
                {
                    priceId: product.prices[0].id, // '25a02633-b875-42e2-802e-32ebef97775a'
                    quantity: 1,
                },
            ],
            returnUrl,
        };

        BillingService.createStripeCheckoutSession(params).then(stripeSession => {
            BillingService.redirectToStripeSession(stripeSession);
        });
    }

    private openLink(linkType: AonLinkType) {
        const _openLink = () => {
            let link;
            if (linkType === AonLinkType.PremiumHub) {
                link = this.data.premium!!.link;
            } else if (linkType === AonLinkType.FreemiumHub) {
                link = this.data.freemium.link;
            } else if (linkType === AonLinkType.PremiumReport) {
                link = this.data.premium!!.reportLink;
            }
            window.open(link, '_blank');
        };

        if (!this.data.consented) {
            // Open dialog for asking for consent
            DialogService.openAssessmentConsentDialog(this, {
                consented: (ctxData: AonContextData) => {
                    AnalyticService.impressionEvent(
                        AnalyticService.IMPRESSION_ACTIONS.ASSESSMENT_AON_CONSENT
                    );
                    this.updateData(ctxData);
                    _openLink();
                },
                close: () => {
                    RestX.getInterfaces() // @ts-ignore
                        .dialogModal.getState()
                        .resolver.res();
                },
            });
            return;
        }
        _openLink();
    }

    giveAonConsent(params: ContextParams = {}): Promise<AonContextData> {
        return axios({
            method: 'PUT',
            url: `${API_URL}candidate/test/aon`.concat(objectToQueryString(params)),
            data: {
                consented: true,
            },
        }).then((res: AxiosResponse<AonContextData>) => {
            this.updateData(res.data);
            return res.data;
        });
    }

    refresh(params?: ContextParams, updateData: boolean = true): Promise<AonContextData> {
        return AonContext.fetchAonContextData(params).then((ctxData: AonContextData) => {
            if (updateData) {
                this.updateData(ctxData);
            }
            return ctxData;
        });
    }

    static fetchAonContextData(params?: ContextParams): Promise<AonContextData> {
        return axios({
            method: 'GET',
            url: `${API_URL}candidate/test/aon`.concat(objectToQueryString(params)),
        }).then((res: AxiosResponse<AonContextData>) => res.data);
    }

    static fetchAonProductFromBilling(): Promise<Product | null> {
        // @ts-ignore
        const assessmentModule = RestX.getInterfaces().assessmentModule;

        const cachedProduct: Product | null = assessmentModule.getAonProduct();
        if (cachedProduct) {
            return Promise.resolve(cachedProduct);
        }

        return BillingService.getProductsByTags({
            category: 'aon',
            version: 'v1',
        }).then(products => {
            if (products.length === 0) {
                return null;
            }

            assessmentModule.setAonProduct(products[0]);
            return products[0];
        });
    }

    static async new(params?: ContextParams): Promise<AonContext> {
        return AonContext.fetchAonContextData(params).then(async ctxData => {
            const context = new AonContext(ctxData);

            // @ts-ignore
            RestX.getInterfaces().assessmentModule.setAonContext(context);

            return context;
        });
    }
}
